import { useEffect, useRef, useState } from "react";
import { leaderboardRefreshTime, roomInformation } from "../../Config/config";
import { InputEscapeResult } from "../../Types/api";
import useLeaderboard from "../../Hooks/useLeaderboard";
import { formatTimeToMinutes } from "../../Helpers/helpers"
import "./leaderboard.css"

type LeaderboardProps = {
  auto: boolean;
  admin: boolean;
};

const Leaderboard = ({ auto, admin }: LeaderboardProps) => {

  const {
    selectedRoom,
    setSelectedRoom,
    selectedRoomLeaderboardData,
    setSelectedRoomLeaderboardData,
    actionMenuOpen,
    handleActionMenuToggle,
    handleDelete,
    getLeaderboardResults
  } = useLeaderboard();


  const [currentIndex, setCurrentIndex] = useState(0);

  // fetchSelectedRoomLeaderboardData always builds a brand-new Map (even when
  // the underlying data hasn't materially changed), so a rotation effect keyed
  // on selectedRoomLeaderboardData gets torn down and recreated on every
  // ~10s poll -- the rotation interval never survives long enough to fire its
  // own tick, so rooms never actually advance. Read the latest data via a ref
  // from inside a rotation interval that runs on its own stable cadence
  // instead, decoupled from fetch churn.
  const roomDataRef = useRef(selectedRoomLeaderboardData);
  useEffect(() => {
    roomDataRef.current = selectedRoomLeaderboardData;
  }, [selectedRoomLeaderboardData]);

  // Keep selectedRoom valid as data arrives/changes: pick an initial room
  // once real data exists, and re-pick if the current selection disappears
  // (e.g. its results were deleted). No-op (bails out of the state update)
  // once the current selection is already valid.
  useEffect(() => {
    if (!auto) return;
    const roomKeys = Array.from(selectedRoomLeaderboardData.keys());
    if (roomKeys.length === 0) return;
    setSelectedRoom(prev => (prev && roomKeys.includes(prev) ? prev : roomKeys[0]));
  }, [auto, selectedRoomLeaderboardData]);

  useEffect(() => {
    if (!auto) return;

    const intervalId = setInterval(() => {
      const roomKeys = Array.from(roomDataRef.current.keys());
      if (roomKeys.length === 0) return; // nothing to rotate through yet

      setCurrentIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % roomKeys.length;
        setSelectedRoom(roomKeys[nextIndex]);
        return nextIndex;
      });
    }, leaderboardRefreshTime);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [auto]);

  const fetchSelectedRoomLeaderboardData = async () => {
    try {
      const data = await getLeaderboardResults();
      const roomMap = new Map<string, InputEscapeResult[]>();

      for (const entry of data) {
        for (const [room, timeTakenStr] of Object.entries(entry.results)) {
          const timeTaken = parseInt(timeTakenStr); // Convert string to number

          const transformed: InputEscapeResult = {
            name: entry.name,
            event: entry.event,
            room,
            school: entry.school,
            timeTaken,
          };

          const roomEntries = roomMap.get(room) ?? [];
          roomEntries.push(transformed);
          roomMap.set(room, roomEntries);
        }
      }

      setSelectedRoomLeaderboardData(roomMap);

    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {

    fetchSelectedRoomLeaderboardData();
    const intervalId = setInterval(
      fetchSelectedRoomLeaderboardData,
      leaderboardRefreshTime
    );
    return () => clearInterval(intervalId);
  }, [selectedRoom]);


  // Sort each room's results by timeTaken
  for (const [room, results] of selectedRoomLeaderboardData.entries()) {
    results.sort((a, b) => a.timeTaken - b.timeTaken);
    selectedRoomLeaderboardData.set(room, results); // optional, since arrays are mutable
  }


  const assignRanks = (results: InputEscapeResult[]): (InputEscapeResult & { rank: number })[] => {
    let lastTime = -1;
    let currentRank = 1;

    return results.map((entry, index) => {
      if (entry.timeTaken !== lastTime) {
        currentRank = index + 1;
        lastTime = entry.timeTaken;
      }
      return { ...entry, rank: currentRank };
    });
  };



  const renderLeaderboardItem = (result: InputEscapeResult & { rank: number }, index: string) => {


    const { name, timeTaken, event, room, school } = result;
    const additionalInfo = [school].filter(Boolean).join(" | ");

    let timeBgColour = "";
    switch (result.rank) {
      case 1:
        timeBgColour = "leaderboard-time--gold";
        break;
      case 2:
        timeBgColour = "leaderboard-time--silver";
        break;
      case 3:
        timeBgColour = "leaderboard-time--bronze";
        break;
      default:
        timeBgColour = "leaderboard-time--default";
        break;
    }

    return (
      <div className="leaderboard-row" key={index}>
        <div className="leaderboard-row__main">
          <div className="leaderboard-rank-badge">
            {result.rank <= 3 ? (
              <img
                src={
                  result.rank === 1
                    ? "/images/gold.png"
                    : result.rank === 2
                      ? "/images/silver.png"
                      : "/images/bronze.png"
                }
                alt={`${result.rank === 1
                  ? "Gold"
                  : result.rank === 2
                    ? "Silver"
                    : "Bronze"
                  } Image`}
                width={50}
                height={50}
                className="leaderboard-rank-badge__img"
              />
            ) : (
              result.rank
            )}
          </div>
          <div className="leaderboard-entry">
            <div>
              <div className="leaderboard-entry__name always-white" style={{ fontSize: "22px"}}>{name}</div>
              <div className="leaderboard-entry__meta">
                {additionalInfo}
              </div>
            </div>
          </div>
          {admin && (
            <div className="leaderboard-menu">
              <button
                onClick={(event) => handleActionMenuToggle(index, event)}
                className="always-white leaderboard-menu__button"
              >
                &#x22EE;
              </button>
              {actionMenuOpen === index && (
                <div className="leaderboard-menu__dropdown">
                  <div
                    className="leaderboard-menu__item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(name, event, room);
                    }}
                  >
                    Delete
                  </div>
                </div>
              )}
            </div>)}
        </div>
        <div
          className={`leaderboard-time ${timeBgColour}`}
          style={{ borderTopRightRadius: "10px", borderBottomRightRadius: "10px" }}
        >
          <div className="leaderboard-time__value" style={{ color: "#fff", fontSize: "22px"}}>{formatTimeToMinutes(timeTaken)}</div>
        </div>
      </div>
    );
  };

  const extractName = (name: string): string | null => {
    if (!name) return null;

    if (name.includes(",")) {
      const splitRooms = name.split(",");
      const concatenatedRoomName = splitRooms
        .map((splitRoom) => roomInformation?.[splitRoom]?.name)
        .filter(Boolean)
        .join(" & ");

      return concatenatedRoomName || null;
    } else {
      return roomInformation?.[name]?.name || null;
    }
  };

  const leaderboardDropdown = Array.from(selectedRoomLeaderboardData.keys()).map((room) => {
    const displayName = extractName(room);

    return displayName ? (
      <option key={room} value={room}>
        {displayName}
      </option>
    ) : null;
  });

  return (
    <div className="leaderboard-container">
      <div id="leaderboardResults" className="leaderboard-results">
        <div className="leaderboard-stack">
          {!auto && Array.from(selectedRoomLeaderboardData.keys()).length ? (
            <div className="leaderboard-room-select">
              <div className="leaderboard-room-select__wrapper">
                <select
                  value={selectedRoom}
                  id="roomSelect"
                  name="roomSelect"
                  onChange={(e) => setSelectedRoom(e.target.value)}
                  className="leaderboard-room-select__input" // <-- extra right padding + hide native arrow
                >
                  <option value="">Select a Room</option>
                  {leaderboardDropdown}
                </select>

                {/* Custom chevron, 16px from the right */}
                <svg
                  className="leaderboard-room-select__chevron"
                  viewBox="0 0 20 20" fill="currentColor"
                  style={{ color: "white" }}
                >
                  <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .94 1.16l-4.24 3.36a.75.75 0 0 1-.94 0L5.21 8.39a.75.75 0 0 1 .02-1.18z" />
                </svg>
              </div>
            </div>
          ) : null}
          {

            [...selectedRoomLeaderboardData.entries()]
              .filter(([room]) => !selectedRoom || room === selectedRoom)
              .map(([room, result]) => {
                const rankedResults = assignRanks(result);
                const top3 = rankedResults.slice(0, 3);
                const sortedLeaderboardData = result;

                return (
                  <div key={room} className="room-section">
                    {<h3 className="room-header">{extractName(room)}</h3>}

                    {top3?.map((entry, index) => renderLeaderboardItem(entry, `${entry.room}-${index}`))}

                    {sortedLeaderboardData?.length > 3 && (
                      <div className="leaderboard-divider"></div>
                    )}
                  </div>
                );
              })}

        </div>
        <div className="leaderboard-overflow">
          <div className="leaderboard-stack">
            {selectedRoom &&
              (assignRanks(selectedRoomLeaderboardData.get(selectedRoom) || [])
                .slice(3)
                .map((result, index) => renderLeaderboardItem(result, `${result.room}-${index + 3}`))
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
